import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ActionBadgeVariant } from '../actionsTable/actionEditModel';
import { useTaskFieldEdit } from './TaskFieldEditContext';

const NONE = '__none__';

type TicketSelectFieldProps = {
  title: string;
  fieldName: string;
  options: { label: string; value: string }[];
  rules?: { required?: { value: boolean; message: string } };
  placeholder?: string;
  isClearable?: boolean;
  disabled?: boolean;
  /** Option values that stay visible but cannot be chosen. */
  disabledValues?: string[];
  /** When set, the closed value and each option render as a Badge. */
  optionBadgeVariant?: (value: string) => ActionBadgeVariant;
};

export const TicketSelectField: React.FC<TicketSelectFieldProps> = ({
  title,
  fieldName,
  options,
  rules,
  placeholder,
  isClearable = false,
  disabled,
  disabledValues = [],
  optionBadgeVariant,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { autoPersist, persistTip } = useTaskFieldEdit();
  const error = errors[fieldName]?.message as string | undefined;
  const required = Boolean(rules?.required?.value);
  const blocked = new Set(disabledValues);

  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={fieldName}>
        {title}
        {required ? <span aria-label="Required field"> *</span> : null}
      </FieldLabel>
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field }) => {
          const current =
            field.value == null || field.value === ''
              ? NONE
              : String(field.value);
          const items = [
            ...(isClearable ? [{ value: NONE, label: 'None' }] : []),
            ...options.map((option) => ({
              value: option.value || NONE,
              label: option.label,
            })),
          ];
          return (
            <Select
              value={current}
              items={items}
              disabled={disabled}
              onValueChange={(next) => {
                if (next == null || next === NONE) {
                  field.onChange('');
                } else {
                  field.onChange(String(next));
                }
                if (autoPersist) void persistTip();
              }}
            >
              <SelectTrigger
                id={fieldName}
                className="tw:w-full"
                aria-invalid={error ? true : undefined}
                aria-label={title}
              >
                {optionBadgeVariant && current !== NONE ? (
                  <Badge variant={optionBadgeVariant(current)}>
                    {options.find((option) => option.value === current)
                      ?.label ?? current}
                  </Badge>
                ) : (
                  <SelectValue
                    placeholder={placeholder ?? `Select ${title}`}
                  />
                )}
              </SelectTrigger>
              <SelectContent className="dark">
                {isClearable ? (
                  <SelectItem value={NONE}>None</SelectItem>
                ) : null}
                {options.map((option) => (
                  <SelectItem
                    key={option.value || NONE}
                    value={option.value || NONE}
                    disabled={blocked.has(option.value)}
                  >
                    {optionBadgeVariant ? (
                      <Badge variant={optionBadgeVariant(option.value)}>
                        {option.label}
                      </Badge>
                    ) : (
                      option.label
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }}
      />
      <FieldError>{error}</FieldError>
    </Field>
  );
};
